import { UUID } from 'node:crypto';
import { EventEmitter, Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { CrudService } from './crud.service';

export interface IEmployeesNote {
	note: string;
	startDate: Date;
	endDate: Date;
	employeeIds: string[];
}

export interface IEmployeeNote {
	id: UUID;
	note: string;
	date: Date;
	employeeId: string;
	modifiedDate: Date;
	modifiedByUserId: UUID;
}

interface IEmployeeNoteRequest {
	note: string;
	date: string;
	employeeId: string;
}

interface IEmployeeNotesResponse {
	result: IEmployeeNote[];
}

export interface IEmployeeNotesSavedArgs {
	succeded: IEmployeeNoteResponse[];
	error: IEmployeeNoteResponse[];
}

interface IEmployeeNoteResponse {
	result: IEmployeeNote;
	error: boolean;
}

export interface IEmployeeNotesChangedArgs {
	date: Date;
	employeeId: UUID;
}

@Injectable({
	providedIn: 'root',
})
export class EmployeeNoteService {
	constructor(private crudService: CrudService) {}

	notesSaved: EventEmitter<IEmployeeNotesSavedArgs> = new EventEmitter();
	onEmployeeNotesSaved(employeeNotesSavedArgs: IEmployeeNotesSavedArgs) {
		this.notesSaved.emit(employeeNotesSavedArgs);
	}

	notesChanged: EventEmitter<IEmployeeNotesChangedArgs> = new EventEmitter();
	onEmployeeNotesChanged(emploeeNoteChangedArgs: IEmployeeNotesChangedArgs) {
		this.notesChanged.emit(emploeeNoteChangedArgs);
	}

	getNotesByEmployeeAndDate(employeeId: string, date: Date): Observable<IEmployeeNote[]> {
		const r = this.crudService.get<IEmployeeNotesResponse>(`/mes/employees/${employeeId}/notes?date=${date.toISOString().substring(0, 10)}`);
		return r.pipe(map((c) => c.result));
	}

	createNote(employeeNote: IEmployeeNote): Observable<IEmployeeNote> {
		const enr: IEmployeeNoteRequest = {
			note: employeeNote.note,
			employeeId: employeeNote.employeeId,
			date: employeeNote.date.toISOString(),
		};
		return this.crudService.create<IEmployeeNoteRequest, IEmployeeNoteResponse>(enr, '/mes/employeenotes').pipe(map((c) => c.result));
	}

	updateNote(employeeNote: IEmployeeNote): Observable<IEmployeeNote> {
		const enr: IEmployeeNoteRequest = {
			note: employeeNote.note,
			employeeId: employeeNote.employeeId,
			date: employeeNote.date.toISOString(),
		};
		return this.crudService.update<IEmployeeNoteRequest, IEmployeeNoteResponse>(enr, `/mes/employeenotes/${employeeNote.id}`).pipe(map((c) => c.result));
	}

	deleteNote(id: UUID): Observable<object> {
		return this.crudService.delete(`/mes/employeenotes/${id}`);
	}

	createNotes(employeesNote: IEmployeesNote): void {
		const requests: Observable<IEmployeeNoteResponse>[] = [];
		const errorResponse: IEmployeeNoteResponse[] = [];
		const response: IEmployeeNoteResponse[] = [];
		const days = Math.round((employeesNote.endDate.getTime() - employeesNote.startDate.getTime()) / (1000 * 3600 * 24)) + 1;
		employeesNote.employeeIds.forEach((id: UUID) => {
			let day = 0;
			let date = new Date(employeesNote.startDate);
			do {
				const enr: IEmployeeNoteRequest = {
					note: employeesNote.note,
					employeeId: id,
					date: date.toISOString(),
				};
				const r = this.crudService.create<IEmployeeNoteRequest, IEmployeeNoteResponse>(enr, '/mes/employeenotes').pipe(
					catchError((_): Observable<IEmployeeNoteResponse> => {
						const fallbackValue: IEmployeeNoteResponse = {
							result: {
								id: null,
								note: enr.note,
								date: new Date(enr.date),
								employeeId: enr.employeeId,
								modifiedByUserId: null,
								modifiedDate: null,
							},
							error: true,
						};
						return of(fallbackValue);
					}),
				);
				requests.push(r);
				date = new Date(date.setDate(date.getDate() + 1));
				day++;
			} while (day < days);
		});

		forkJoin(requests).subscribe({
			next: (e) => {
				e.forEach((c) => {
					if (!c.error) {
						this.onEmployeeNotesChanged({
							employeeId: c.result.employeeId as UUID,
							date: new Date(c.result.date),
						});
						response.push(c);
					} else {
						errorResponse.push(c);
					}
				});
			},
			complete: () => {
				this.onEmployeeNotesSaved({ succeded: response, error: errorResponse });
			},
		});
	}
}
